import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CONTENT, PALETTE, IMAGES } from "../../lib/content";
import { useLang } from "../../lib/LangContext";
import { ChapterHead, Reveal, ChapterShell } from "./Shell";
import { Wordmark, MirrorR } from "../Logo";

/* ---------- IX. PALETTE ---------- */
export function PaletteChapter() {
    const { lang } = useLang();
    const c = CONTENT.palette[lang];
    return (
        <ChapterShell slug="palette">
            <div className="col-span-12 md:col-span-11">
                <ChapterHead eyebrow={c.eyebrow} title={c.title} roman="IX" />
            </div>
            <div className="col-span-12 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-16">
                {PALETTE.map((p, i) => {
                    const light =
                        p.hex === "#F4EFE6" ||
                        p.hex === "#FBF9F4" ||
                        p.hex === "#D9CDB8";
                    return (
                        <Reveal key={i} delay={i * 0.06}>
                            <div>
                                <div className="aspect-[4/5] border" style={{ background: p.hex, borderColor: "rgba(11,11,11,0.15)" }}>
                                    <div className="p-4 h-full flex items-end">
                                        <span className="font-didone italic text-2xl" style={{ color: light ? "#0B0B0B" : "#F4EFE6", opacity: 0.7 }}>
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <h4 className="font-didone text-xl md:text-2xl">{p.name}</h4>
                                    <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 font-micro text-[9px] opacity-80" style={{ letterSpacing: "0.2em" }}>
                                        <div>HEX</div><div>{p.hex}</div>
                                        <div>CMYK</div><div>{p.cmyk}</div>
                                        <div>RGB</div><div>{p.rgb}</div>
                                        <div>PANTONE</div><div>{p.pantone}</div>
                                    </div>
                                </div>
                            </div>
                        </Reveal>
                    );
                })}
            </div>
        </ChapterShell>
    );
}

/* ---------- X. TYPOGRAPHY ---------- */
export function TypographyChapter() {
    const { lang } = useLang();
    const c = CONTENT.typography[lang];
    return (
        <ChapterShell slug="typography">
            <div className="col-span-12 md:col-span-11">
                <ChapterHead eyebrow={c.eyebrow} title={c.title} roman="X" />
            </div>

            {/* Didone */}
            <div className="col-span-12 mt-4">
                <Reveal>
                    <div className="border-t border-b py-16" style={{ borderColor: "rgba(201,169,106,0.5)" }}>
                        <div className="flex items-baseline justify-between gap-6 mb-8">
                            <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                                Aa — BODONI MODA
                            </div>
                            <div className="font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.32em" }}>
                                FOOD · HERITAGE
                            </div>
                        </div>
                        <p className="font-didone leading-[0.9] tracking-tight" style={{ fontSize: "clamp(4rem, 14vw, 12rem)" }}>
                            Aa
                        </p>
                        <p className="mt-8 font-didone" style={{ fontSize: "clamp(1.6rem, 4vw, 3.5rem)" }}>
                            {c.didone.specimen}
                        </p>
                        <p className="mt-8 font-serif-lux text-lg md:text-xl max-w-3xl opacity-85">{c.didone.use}</p>
                    </div>
                </Reveal>
            </div>

            {/* Contemporary */}
            <div className="col-span-12 mt-10">
                <Reveal delay={0.15}>
                    <div className="border-t border-b py-16" style={{ borderColor: "rgba(201,169,106,0.5)" }}>
                        <div className="flex items-baseline justify-between gap-6 mb-8">
                            <div className="font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                                Aa — CORMORANT GARAMOND
                            </div>
                            <div className="font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.32em" }}>
                                BEAUTY · HOME
                            </div>
                        </div>
                        <p className="font-serif-lux italic leading-[0.9]" style={{ fontSize: "clamp(4rem, 14vw, 12rem)" }}>
                            Aa
                        </p>
                        <p className="mt-8 font-serif-lux italic" style={{ fontSize: "clamp(1.6rem, 4vw, 3.5rem)" }}>
                            {c.contemporary.specimen}
                        </p>
                        <p className="mt-8 font-serif-lux text-lg md:text-xl max-w-3xl opacity-85">{c.contemporary.use}</p>
                    </div>
                </Reveal>
            </div>
        </ChapterShell>
    );
}

/* ---------- XI. PHOTOGRAPHY ---------- */
export function PhotographyChapter() {
    const { lang } = useLang();
    const c = CONTENT.photography[lang];
    const ref = React.useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y1 = useTransform(scrollYProgress, [0, 1], [60, -60]);
    const y2 = useTransform(scrollYProgress, [0, 1], [-40, 40]);

    return (
        <ChapterShell slug="photography">
            <div className="col-span-12 md:col-span-11">
                <ChapterHead eyebrow={c.eyebrow} title={c.title} roman="XI" />
            </div>
            <div className="col-span-12 md:col-span-6" ref={ref}>
                <motion.div style={{ y: y1 }}>
                    <Reveal>
                        <div className="aspect-[3/4] overflow-hidden">
                            <img src={IMAGES.salt2} alt="Salt pans of Trapani at dusk" className="w-full h-full object-cover" style={{ filter: "contrast(1.08) saturate(0.5) sepia(0.1)" }} />
                        </div>
                        <p className="mt-3 font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.32em" }}>
                            Fig. 01 · Saline · Trapani
                        </p>
                    </Reveal>
                </motion.div>
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-8">
                <motion.div style={{ y: y2 }} className="mt-16 md:mt-40">
                    <Reveal delay={0.15}>
                        <p className="font-serif-lux text-2xl md:text-3xl leading-[1.35]">{c.body}</p>
                        <ul className="mt-10 space-y-3">
                            {c.cues.map((cue, i) => (
                                <li key={i} className="font-serif-lux italic text-lg opacity-90 flex gap-4">
                                    <span className="font-didone text-sm" style={{ color: "#C9A96A" }}>—</span>
                                    <span>{cue}</span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                </motion.div>
            </div>

            {/* Second row */}
            <div className="col-span-12 md:col-span-5 mt-16">
                <Reveal>
                    <div className="aspect-square overflow-hidden">
                        <img src={IMAGES.olive1} alt="Olive grove" className="w-full h-full object-cover" style={{ filter: "contrast(1.05) saturate(0.5) sepia(0.1)" }} />
                    </div>
                    <p className="mt-3 font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.32em" }}>
                        Fig. 02 · Uliveto
                    </p>
                </Reveal>
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-7 mt-16">
                <Reveal delay={0.1}>
                    <div className="aspect-[3/4] overflow-hidden">
                        <img src={IMAGES.hands1} alt="Artisan hands" className="w-full h-full object-cover" style={{ filter: "contrast(1.05) saturate(0.4) sepia(0.15)" }} />
                    </div>
                    <p className="mt-3 font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.32em" }}>
                        Fig. 03 · Mani
                    </p>
                </Reveal>
            </div>
            <div className="col-span-12 md:col-span-2 md:col-start-11 mt-16">
                <Reveal delay={0.2}>
                    <div className="aspect-[3/4] overflow-hidden">
                        <img src={IMAGES.olive2} alt="Olive tree" className="w-full h-full object-cover" style={{ filter: "contrast(1.05) saturate(0.5) sepia(0.1)" }} />
                    </div>
                    <p className="mt-3 font-micro text-[10px] opacity-70" style={{ letterSpacing: "0.32em" }}>
                        Fig. 04
                    </p>
                </Reveal>
            </div>
        </ChapterShell>
    );
}

/* ---------- XII. PACKAGING ---------- */
export function PackagingChapter() {
    const { lang } = useLang();
    const c = CONTENT.packaging[lang];
    return (
        <ChapterShell slug="packaging" dark>
            <div className="col-span-12 md:col-span-11">
                <ChapterHead eyebrow={c.eyebrow} title={c.title} roman="XII" dark />
            </div>
            <div className="col-span-12 md:col-span-7 mt-4">
                <Reveal>
                    <p className="font-serif-lux text-2xl md:text-3xl leading-[1.35]" style={{ color: "#F4EFE6" }}>
                        {c.body}
                    </p>
                </Reveal>
            </div>

            {/* Bottle spotlight image */}
            <div className="col-span-12 md:col-span-6 mt-20">
                <Reveal>
                    <div className="aspect-[3/4] overflow-hidden relative" style={{ background: "#0B0B0B" }}>
                        <img src={IMAGES.bottle1} alt="Bottle" className="w-full h-full object-cover opacity-80" style={{ filter: "brightness(0.85) contrast(1.1)" }} />
                        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 40%, transparent 20%, rgba(11,11,11,0.75) 80%)" }} />
                        <div className="absolute bottom-6 left-6 font-micro text-[10px]" style={{ color: "#C9A96A", letterSpacing: "0.4em" }}>
                            250 ML · MONOCULTIVAR · OPAQUE BLACK
                        </div>
                    </div>
                </Reveal>
            </div>

            {/* Formats list */}
            <div className="col-span-12 md:col-span-5 md:col-start-8 mt-20">
                <Reveal delay={0.1}>
                    <div className="font-micro text-[10px] mb-6" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                        {lang === "en" ? "FORMATS" : "FORMATI"}
                    </div>
                    <div>
                        {c.formats.map((f, i) => (
                            <div key={i} className="flex items-baseline justify-between py-4 border-b" style={{ borderColor: "rgba(201,169,106,0.35)" }}>
                                <span className="font-didone text-xl md:text-2xl" style={{ color: "#F4EFE6" }}>
                                    {f.label}
                                </span>
                                <span className="font-serif-lux italic text-lg md:text-xl" style={{ color: "#C9A96A" }}>
                                    {f.spec}
                                </span>
                            </div>
                        ))}
                    </div>
                    <p className="mt-8 font-serif-lux italic opacity-70 text-sm" style={{ color: "#D9CDB8" }}>
                        {c.note}
                    </p>
                </Reveal>
            </div>

            {/* Layers */}
            <div className="col-span-12 mt-24">
                <Reveal>
                    <div className="font-micro text-[10px] mb-8" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                        {lang === "en" ? "SIX LAYERS OF THE RITUAL" : "SEI STRATI DEL RITO"}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6">
                        {c.layers.map((l, i) => (
                            <div key={i} className="border-t pt-4" style={{ borderColor: "rgba(201,169,106,0.35)" }}>
                                <span className="font-didone italic text-sm" style={{ color: "#C9A96A" }}>
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <p className="mt-3 font-serif-lux text-lg" style={{ color: "#F4EFE6" }}>
                                    {l}
                                </p>
                            </div>
                        ))}
                    </div>
                </Reveal>
            </div>

            {/* Wax seal illustration */}
            <div className="col-span-12 mt-24 flex justify-center">
                <Reveal>
                    <div className="relative flex items-center justify-center">
                        <div
                            className="rounded-full flex items-center justify-center"
                            style={{
                                width: 260,
                                height: 260,
                                background: "radial-gradient(circle at 40% 35%, #1a1a1a 0%, #0B0B0B 60%, #050505 100%)",
                                boxShadow: "inset 0 0 40px rgba(0,0,0,0.6)",
                            }}
                        >
                            <span className="font-didone leading-none" style={{ transform: "scaleX(-1)", display: "inline-block", fontSize: 160, color: "#C9A96A", opacity: 0.9 }}>
                                R
                            </span>
                        </div>
                    </div>
                    <p className="text-center mt-6 font-micro text-[10px]" style={{ letterSpacing: "0.4em", color: "#C9A96A" }}>
                        {lang === "en" ? "MATTE BLACK WAX SEAL" : "SIGILLO DI CERA NERA OPACA"}
                    </p>
                </Reveal>
            </div>
        </ChapterShell>
    );
}

/* ---------- XIII. PRODUCT PHILOSOPHY ---------- */
export function ProductChapter() {
    const { lang } = useLang();
    const c = CONTENT.product[lang];
    return (
        <ChapterShell slug="product">
            <div className="col-span-12 md:col-span-11">
                <ChapterHead eyebrow={c.eyebrow} title={c.title} roman="XIII" />
            </div>
            <div className="col-span-12 grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-12">
                {c.lines.map((l, i) => (
                    <Reveal key={i} delay={i * 0.1}>
                        <div className="border-t pt-6" style={{ borderColor: "rgba(11,11,11,0.4)" }}>
                            <div className="font-didone italic text-lg" style={{ color: "#C9A96A" }}>
                                {l.n}
                            </div>
                            <h4 className="mt-4 font-didone text-3xl md:text-4xl">{l.label}</h4>
                            <p className="mt-4 font-serif-lux text-lg opacity-85">{l.body}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
        </ChapterShell>
    );
}
